import React, { createContext, useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { LoaderCircle, LockKeyhole } from 'lucide-react';
import { Usuario as UsuarioDTO } from '@/types/usuario';
import { UsuarioService } from '@/services';
import { keycloak } from '@/keycloak';
import { mensagemDeErro } from '@/utils/erro-api';
import { Button } from '@/components/ui/button';

export interface AuthContextType {
    user: UsuarioDTO | null;
    signout: () => void;
    alterarSenha: () => void;
}

export let AuthContext = createContext<AuthContextType>(null!);

const signout = () => keycloak.logout({ redirectUri: window.location.origin });

// Tela de definir senha do Keycloak; ao salvar ou cancelar, volta para a mesma página
const alterarSenha = () => keycloak.login({ action: 'UPDATE_PASSWORD' });

// A sessão vem do Keycloak (index.tsx só renderiza depois do login); aqui carrega o usuário e as empresas dele
export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<UsuarioDTO | null>(null);
    const [erro, setErro] = useState<AxiosError>();

    useEffect(() => {
        UsuarioService.current().then(r => setUser(r.data)).catch(setErro);
    }, []);

    const semAcesso = erro || (user && !user.empresas?.length);
    if (semAcesso) {
        return (
            <div className="flex min-h-screen flex-col items-center justify-center gap-4 p-6 text-center">
                <LockKeyhole className="h-10 w-10 text-muted-foreground" />
                <div>
                    <h1 className="text-lg font-semibold">Sem acesso ao Vigento</h1>
                    <p className="mt-1 max-w-sm text-sm text-muted-foreground">
                        {erro ? mensagemDeErro(erro) : 'Seu usuário ainda não está liberado em nenhuma corretora. Peça acesso ao administrador.'}
                    </p>
                </div>
                <Button variant="outline" onClick={signout}>Sair</Button>
            </div>
        );
    }

    if (!user) {
        return <div className="flex min-h-screen items-center justify-center"><LoaderCircle className="h-6 w-6 animate-spin text-muted-foreground" /></div>;
    }

    return <AuthContext.Provider value={{ user, signout, alterarSenha }}>{children}</AuthContext.Provider>;
}

export default AuthProvider;
